<template>
	<div
		v-if="store.weldlines.filter((w) => w.selected).length > 0"
		style="display: flex; flex-direction: column; gap: 4px; margin-top: 12px"
	>
		<div>
			<strong>{{ $t("weld.weldlines.existingWeldlinesTitle") }}</strong>
		</div>
		<div style="margin-top: 4px; display: flex; align-items: center">
			<input
				type="checkbox"
				:disabled="store.article.isReadOnly"
				:checked="areAllWeldlinesCleaned"
				:indeterminate="areSomeWeldlinesCleaned"
				@change="handleCleanCheckboxChange"
			/>
			<span style="margin-left: 4px">{{ $t("weld.weldlines.cleanAllCheckboxLabel") }}</span>
		</div>
		<div
			v-for="(weldline, index) in store.weldlines.filter((w) => w.selected)"
			:key="index"
			class="weldline-feature"
			@mouseenter="setWeldlineHoverState(weldline.weldlineId!, true)"
			@mouseleave="setWeldlineHoverState(weldline.weldlineId!, false)"
		>
			<!-- This first div uses all available space and contains the line info and corner settings -->
			<div
				style="flex-grow: 1; display: flex; flex-wrap: wrap; align-items: center; gap: 4px"
			>
				<div>
					<span>{{ $t("weld.weldlines.weldlineLabel") }} </span>
					<span v-if="weldline.isFiller"> {{ $t("weld.weldlines.fillerLabel") }} </span>
					<span v-else> {{ Math.round(weldline.length * 10) / 10 }}mm </span>
				</div>
				<div v-if="weldline.numberOfSegments > 1" style="margin-left: 4px">
					{{ weldline.numberOfSegments }}*{{ weldline.lengthOfSegment }} ({{
						getFreeSpace(weldline)
					}})
				</div>
				<div v-if="weldline.isPlugged" style="margin-left: 4px">{{ $t("weld.weldlines.pluggedLabel") }}</div>
				<div
					v-if="weldline.weldCorners && weldline.weldCorners.length > 0"
					style="margin-left: 4px"
				>
					{{ $t("weld.weldlines.withCornersLabel") }}
				</div>
				<div v-for="(corner, index) in weldline.weldCorners" :key="index">
					<div class="tooltip">
						<div
							style="
								display: flex;
								flex-wrap: nowrap;
								align-items: center;
								gap: 4px;
								margin-left: 4px;
							"
						>
							<input
								type="checkbox"
								:checked="corner.weldCorner && corner.cornerPoints.length > 0"
								:disabled="
									store.article.isReadOnly ||
									!(corner.cornerPoints && corner.cornerPoints.length > 0)
								"
								@change="
									updateWeldlineCornerState(
										weldline.weldlineId!,
										corner.position,
										$event
									)
								"
							/>
							<span>{{ $t("weld.weldlines.cornerLabel") }}&nbsp;{{ corner.position + 1 }}</span>
						</div>
						<span class="tooltiptext">
							<span v-if="corner.cornerPoints && corner.cornerPoints.length > 0">
								{{ $t("weld.weldlines.cornerTooltipChoose") }}
							</span>
							<span v-else>
								{{ $t("weld.weldlines.cornerTooltipTooLarge") }}
								<a
									href="https://blexon.com/blechteile-schweissen"
									rel="noreferrer"
									target="_blank"
									style="color: var(--blexon-color)"
								>
									{{ $t("weld.weldlines.cornerTooltipConstructionLink") }}</a
								>
							</span>
						</span>
					</div>
				</div>
			</div>
			<div v-if="!store.article.isReadOnly && weldline.curve && weldline.curve.length === 2">
				<button
					class="icon-button"
					:title="$t('weld.weldlines.defineSegmentsTitle')"
					@click="openWeldlineSegmentDialog(weldline)"
				>
					<svg-icon type="mdi" :size="20" :path="mdiSegment"></svg-icon>
				</button>
			</div>
			<div v-if="!store.article.isReadOnly">
				<button
					class="icon-button"
					:title="$t('weld.weldlines.deleteWeldlineTitle')"
					@click="deleteWeldline(weldline.weldlineId!)"
				>
					<svg-icon type="mdi" :size="20" :path="mdiDelete"></svg-icon>
				</button>
			</div>
		</div>

		<!-- Segment Dialog -->
		<div v-if="segmentDialog" class="modal-overlay">
			<div class="modal">
				<h3>{{ $t("weld.weldlines.segmentDialogTitle") }}</h3>
				<p>
					{{ $t("weld.weldlines.segmentDialogInfo1") }}<br />
					{{ $t("weld.weldlines.segmentDialogInfo2") }}
				</p>
				<div>
					<span>{{ $t("weld.weldlines.numberOfSegmentsLabel") }} </span>
					<input
						type="number"
						v-model="numberOfSegments"
						min="0"
						@change="validateWeldlineDialog()"
					/>
				</div>
				<div style="margin-top: 8px">
					<span>{{ $t("weld.weldlines.segmentLengthLabel") }} </span>
					<input
						type="number"
						:disabled="numberOfSegments === 1"
						v-model="lengthOfSegment"
						min="5"
						@change="validateWeldlineDialog()"
					/>
				</div>
				<div v-if="numberOfSegments === 1" style="margin-top: 4px">
					{{ $t("weld.weldlines.singleSegmentInfo") }}
				</div>
				<div v-if="weldlineDialogError.length > 0" style="color: red; margin-top: 4px">
					{{ weldlineDialogError }}
				</div>
				<div style="display: flex; flex-direction: row-reverse; gap: 8px; margin-top: 16px">
					<button
						@click="confirmWeldlineSegments()"
						:disabled="!isSegmentValid"
						class="blexon-button"
					>
						{{ $t("weld.weldlines.applyButton") }}
					</button>
					<button
						@click="
							() => {
								setWeldlineHoverState(dialogWeldlineId, false);
								segmentDialog = false;
							}
						"
					>
						{{ $t("weld.weldlines.cancelButton") }}
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { useMainStore } from "@/store/mainStore";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import SvgIcon from "@jamescoyle/vue-icon";
import { mdiSegment, mdiDelete } from "@mdi/js";
import { IWeldlineDto } from "@/model/IWeldlineDto";

const store = useMainStore();
const { t } = useI18n();

// props for segment dialog
const segmentDialog = ref<boolean>(false);
const dialogWeldlineId = ref<number>(0);
const numberOfSegments = ref<number>(0);
const lengthOfSegment = ref<number>(0);
const weldlineDialogError = ref<string>("");

const areAllWeldlinesCleaned = computed(() => {
	return store.weldlines.every((w) => w.clean);
});
const areSomeWeldlinesCleaned = computed(() => {
	return !areAllWeldlinesCleaned && store.weldlines.some((w) => w.clean);
});

const handleCleanCheckboxChange = (event: Event) => {
	const target = event.target as HTMLInputElement;
	// If the checkbox is indeterminate (some are cleaned), a click will check it.
	store.weldlines.forEach((w) => (w.clean = target.checked));
	store.isFileSaved = false;
};
const getFreeSpace = (weldline: IWeldlineDto): number => {
	const roundedLength = Math.round(weldline.length * 10) / 10;
	const freeSpace =
		(roundedLength - weldline.numberOfSegments * weldline.lengthOfSegment) /
		(weldline.numberOfSegments - 1);
	return Math.round(freeSpace * 10) / 10;
};
const updateWeldlineCornerState = (weldlineId: number, cornerPosition: number, event: Event) => {
	const target = event.target as HTMLInputElement;
	const weldline = store.weldlines.filter((w) => w.weldlineId === weldlineId)[0];
	const corner = weldline.weldCorners!.filter((c) => c.position === cornerPosition)[0];
	corner.weldCorner = target.checked;
	for (const otherWeldline of store.weldlines.filter((l) => l.weldlineId !== weldlineId)) {
		for (const otherCorner of otherWeldline.weldCorners!) {
			if (
				otherCorner.cornerPoints.every((p) =>
					corner.cornerPoints.some((cp) => cp.equals(p))
				)
			) {
				otherCorner.weldCorner = target.checked;
			}
		}
	}
	store.isFileSaved = false;
};
const deleteWeldline = (weldlineId: number) => {
	const weldlineIndex = store.weldlines.findIndex((w) => w.weldlineId === weldlineId);
	if (weldlineIndex > -1) {
		store.weldlines.splice(weldlineIndex, 1);
		store.isFileSaved = false;
	}
};
const openWeldlineSegmentDialog = (weldline: IWeldlineDto) => {
	weldlineDialogError.value = "";
	dialogWeldlineId.value = weldline.weldlineId!;
	lengthOfSegment.value = Math.round(weldline.lengthOfSegment * 10) / 10;
	numberOfSegments.value = weldline.numberOfSegments;
	segmentDialog.value = true;
	setTimeout(() => {
		setWeldlineHoverState(weldline.weldlineId!, true);
	}, 100);
};

const confirmWeldlineSegments = () => {
	validateWeldlineDialog();
	if (!isSegmentValid) {
		return;
	}
	const currentWeldline = store.weldlines.find((w) => w.weldlineId === dialogWeldlineId.value);
	currentWeldline!.numberOfSegments = numberOfSegments.value;
	currentWeldline!.lengthOfSegment = lengthOfSegment.value;
	store.isFileSaved = false;
	setWeldlineHoverState(currentWeldline!.weldlineId!, false);
	segmentDialog.value = false;
};

const isSegmentValid = computed((): boolean => {
	return weldlineDialogError.value === "";
});

const validateWeldlineDialog = () => {
	const currentWeldline = store.weldlines.find((w) => w.weldlineId === dialogWeldlineId.value);
	if (!currentWeldline) {
		return;
	}
	// Round the length input to the nearest 10th
	lengthOfSegment.value = Math.round(lengthOfSegment.value * 10) / 10;
	const lengthOfCurrentWeldline = Math.round(currentWeldline.length * 10) / 10;
	// Automatically adjust the length of the segment to the length of the
	// weldline, if only one segment is chosen
	if (numberOfSegments.value === 1) {
		lengthOfSegment.value = lengthOfCurrentWeldline;
	}
	if (!Number.isInteger(numberOfSegments.value)) {
		weldlineDialogError.value = t("weld.weldlines.errorSegmentsMustBeInteger");
		return;
	}
	if (numberOfSegments.value < 1) {
		weldlineDialogError.value = t("weld.weldlines.errorSegmentsMinOne");
		return;
	}
	if (lengthOfSegment.value < 5) {
		weldlineDialogError.value = t("weld.weldlines.errorSegmentMinLength");
		return;
	}
	if (
		numberOfSegments.value * lengthOfSegment.value + (numberOfSegments.value - 1) * 5 >
		lengthOfCurrentWeldline
	) {
		weldlineDialogError.value = t("weld.weldlines.errorSegmentsTotalTooLong");
		return;
	}
	weldlineDialogError.value = "";
};

const setWeldlineHoverState = (weldlineId: number, state: boolean) => {
	const currentWeldline = store.weldlines.find((w) => w.weldlineId === weldlineId);
	if (!currentWeldline) return;
	currentWeldline!.hovered = state;
};
</script>

<style scoped>
.delete-button {
	background-color: transparent;
	box-shadow: none;
	border: none;
	padding: 0px;
}

.weldline-feature {
	border: 1px solid #efbb20;
	border-radius: 2px;
	padding: 4px;
	display: flex;
	gap: 4px;
	align-items: center;
}
</style>
